import { DiffLine } from './diff.service';
import { CodingNote } from './coding-answer.model';

/** A run of characters inside one line; `changed` parts are highlighted. */
export interface CodeSegment {
  text: string;
  changed: boolean;
}

export interface DiffRow {
  kind: 'context' | 'remove' | 'add';
  oldLine?: number;
  newLine?: number;
  segments: CodeSegment[];
  /** True when this removed/added line was matched with a similar line on the other side (inline highlights). */
  paired?: boolean;
}

/**
 * One group of rows rendered together. A `change` hunk carries the exact, deterministic edit
 * steps (old line numbers) plus any AI explanations anchored to it. A `context` hunk is unchanged
 * code and can be collapsed in the middle when long.
 */
export interface DiffHunk {
  kind: 'context' | 'change';
  rows: DiffRow[];
  steps: string[];
  notes: string[];
  head: DiffRow[];
  hidden: DiffRow[];
  tail: DiffRow[];
  expanded: boolean;
}

export interface FileDiff {
  hunks: DiffHunk[];
  /** Number of change hunks. */
  changes: number;
  /** Lines the learner has to touch: every removed line plus added lines that replace nothing. */
  edits: number;
  /** Notes that could not be anchored to a specific hunk. */
  fileNotes: string[];
}

interface Op {
  /** 1-based character column in the old line. */
  col: number;
  removed: string;
  added: string;
}

const CONTEXT_COLLAPSE_AT = 10;
const CONTEXT_EDGE = 3;
const PAIR_THRESHOLD = 0.45;

/**
 * Splits a line into words, spaces and symbols, and names into their parts ("shirt_color" -> shirt, _, color;
 * "wordStart" -> word, Start; "num2" -> num, 2), so only the part of a name that changed is highlighted.
 */
const tokenize = (text: string): string[] =>
  text.match(/\p{Lu}+(?=\p{Lu}\p{Ll})|\p{Lu}?\p{Ll}+|\p{L}+|\p{N}+|_+|\s+|[^\s\p{L}\p{N}_]/gu) || [];

const isSpace = (text: string): boolean => /^\s+$/.test(text);

/** Shows spaces, tabs and carriage returns so an indentation change is unmistakable. */
export function visibleWhitespace(text: string): string {
  return text.replace(/ /g, '·').replace(/\t/g, '→').replace(/\r/g, '␍');
}

/** Bigram similarity of two lines, case-insensitive so a capitalisation fix still pairs the lines. */
function similarity(a: string, b: string): number {
  const x = a.trim().toLowerCase(), y = b.trim().toLowerCase();
  if (x === y) return x ? 1 : 0;
  if (x.length < 2 || y.length < 2) return 0;
  const grams = new Map<string, number>();
  for (let i = 0; i < x.length - 1; i++) {
    const gram = x.slice(i, i + 2);
    grams.set(gram, (grams.get(gram) || 0) + 1);
  }
  let shared = 0;
  for (let i = 0; i < y.length - 1; i++) {
    const gram = y.slice(i, i + 2);
    const count = grams.get(gram) || 0;
    if (count) {
      shared++;
      grams.set(gram, count - 1);
    }
  }
  return (2 * shared) / (x.length - 1 + y.length - 1);
}

/** Longest-common-subsequence alignment of two token lists; returns matched index pairs. */
function align(a: string[], b: string[]): Array<[number, number]> {
  const table = Array.from({ length: a.length + 1 }, () => new Uint16Array(b.length + 1));
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      table[i][j] = a[i] === b[j] ? 1 + table[i + 1][j + 1] : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }
  const matches: Array<[number, number]> = [];
  let i = 0, j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      matches.push([i, j]);
      i++; j++;
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      i++;
    } else {
      j++;
    }
  }
  return matches;
}

function toSegments(tokens: string[], changed: boolean[]): CodeSegment[] {
  const result: CodeSegment[] = [];
  tokens.forEach((text, index) => {
    const last = result[result.length - 1];
    if (last && last.changed === changed[index]) last.text += text;
    else result.push({ text, changed: changed[index] });
  });
  return result;
}

/** Splits whitespace off the edges of a replace op, so "indent by 4 spaces" is reported separately from "change X to Y". */
function splitWhitespace(op: Op): Op[] {
  if (!op.removed || !op.added) return [op];
  const result: Op[] = [];
  let { col, removed, added } = op;
  const leadAdded = (added.match(/^\s+/) || [''])[0];
  const leadRemoved = (removed.match(/^\s+/) || [''])[0];
  if (leadAdded && !leadRemoved) {
    result.push({ col, removed: '', added: leadAdded });
    added = added.slice(leadAdded.length);
  } else if (leadRemoved && !leadAdded) {
    result.push({ col, removed: leadRemoved, added: '' });
    removed = removed.slice(leadRemoved.length);
    col += leadRemoved.length;
  }
  const tailAdded = (added.match(/\s+$/) || [''])[0];
  const tailRemoved = (removed.match(/\s+$/) || [''])[0];
  let tail: Op | null = null;
  if (tailAdded && !tailRemoved) {
    added = added.slice(0, added.length - tailAdded.length);
    tail = { col: col + removed.length, removed: '', added: tailAdded };
  } else if (tailRemoved && !tailAdded) {
    removed = removed.slice(0, removed.length - tailRemoved.length);
    tail = { col: col + removed.length, removed: tailRemoved, added: '' };
  }
  if (removed || added) result.push({ col, removed, added });
  if (tail) result.push(tail);
  return result;
}

/** Unmatched token ranges on both sides of an aligned line: old tokens [i0, i1) became new tokens [j0, j1). */
interface TokenOp { i0: number; i1: number; j0: number; j1: number; }

/**
 * Token-level diff of one changed line: only what actually changes is highlighted, never code both versions
 * share ("m; num <= n" -> "a; num <= b" highlights m / a and n / b, not "; num <="). Edits separated by nothing but
 * spaces are one edit ("a red color shirt" -> "blue shoes"), and so is a reworked expression ("=+" -> "+=").
 */
function inlineDiff(before: string, after: string): { before: CodeSegment[]; after: CodeSegment[]; ops: Op[] } {
  const oldTokens = tokenize(before), newTokens = tokenize(after);
  const matches = align(oldTokens, newTokens);
  const raw: TokenOp[] = [];
  let i = 0, j = 0;
  for (const [mi, mj] of [...matches, [oldTokens.length, newTokens.length] as [number, number]]) {
    if (mi > i || mj > j) raw.push({ i0: i, i1: mi, j0: j, j1: mj });
    i = mi + 1; j = mj + 1;
  }
  const substantive = (op: TokenOp) =>
    !isSpace(oldTokens.slice(op.i0, op.i1).join('') + newTokens.slice(op.j0, op.j1).join(''));
  const plainWords = (tokens: string[]) => tokens.every(token => isSpace(token) || /^[\p{L}\p{N}_]+$/u.test(token));
  /** A word (or words) replaced by a word: a rename or a new value, not a reworked expression. */
  const swap = (op: TokenOp) => op.i0 < op.i1 && op.j0 < op.j1
    && plainWords(oldTokens.slice(op.i0, op.i1)) && plainWords(newTokens.slice(op.j0, op.j1));
  const merged: TokenOp[] = [];
  for (const op of raw) {
    const last = merged[merged.length - 1];
    if (last) {
      const gap = oldTokens.slice(last.i1, op.i0);
      const gapText = gap.join('');
      const gapWords = gap.filter(token => /[\p{L}\p{N}_]/u.test(token)).length;
      const both = substantive(last) && substantive(op);
      // Adjacent edits always merge; edits with only spaces between them merge when both are real code changes
      // (so a whitespace-only tweak stays its own step). Two edits that each rework the expression (symbols
      // changed, or code only added or removed: "=+" -> "+=", '" + count + "' -> "{count}") read as one change
      // across one short shared word. A word swapped for a word ("m" -> "a", "shirt" -> "shoe") always stays
      // separate, so the code it shares with its neighbours is never highlighted.
      if (!gapText || (both && isSpace(gapText))
          || (both && !swap(last) && !swap(op) && gapWords <= 1 && gapText.length <= 12)) {
        last.i1 = op.i1; last.j1 = op.j1;
        continue;
      }
    }
    merged.push({ ...op });
  }
  const oldChanged = oldTokens.map(() => false), newChanged = newTokens.map(() => false);
  const ops: Op[] = [];
  for (const op of merged) {
    for (let k = op.i0; k < op.i1; k++) oldChanged[k] = true;
    for (let k = op.j0; k < op.j1; k++) newChanged[k] = true;
    const col = 1 + oldTokens.slice(0, op.i0).join('').length;
    ops.push(...splitWhitespace({ col, removed: oldTokens.slice(op.i0, op.i1).join(''), added: newTokens.slice(op.j0, op.j1).join('') }));
  }
  return { before: toSegments(oldTokens, oldChanged), after: toSegments(newTokens, newChanged), ops };
}

const quote = (text: string): string => '`' + (isSpace(text) ? visibleWhitespace(text) : text) + '`';

function countSpaces(text: string): string {
  const tabs = (text.match(/\t/g) || []).length;
  const spaces = text.length - tabs;
  const parts = [];
  if (spaces) parts.push(`${spaces} space${spaces === 1 ? '' : 's'}`);
  if (tabs) parts.push(`${tabs} tab${tabs === 1 ? '' : 's'}`);
  return parts.join(' and ') || 'nothing';
}

/** The code just before a column, about a dozen visible characters, keeping its spaces as written. */
function contextBefore(line: string, col: number): string {
  const tokens = tokenize(line.slice(0, col - 1).trimEnd());
  let out = '';
  for (let k = tokens.length - 1; k >= 0 && out.replace(/\s/g, '').length < 12; k--) out = tokens[k] + out;
  return out.trimStart();
}

/** The code just after a removed span, about a dozen visible characters, keeping its spaces as written. */
function contextAfter(line: string, col: number, removed: string): string {
  const tokens = tokenize(line.slice(col - 1 + removed.length).trimStart());
  let out = '';
  for (const token of tokens) {
    if (out.replace(/\s/g, '').length >= 12) break;
    out += token;
  }
  return out.trimEnd();
}

/** Phrases one op without a line prefix, for example: change `PRINT` to `print`. */
function describeOp(op: Op, line: string): string {
  const atStart = op.col === 1;
  const atEnd = op.col - 1 + op.removed.length >= line.length;
  if (!op.removed) {
    if (isSpace(op.added)) {
      if (atStart) return `indent by ${countSpaces(op.added)}`;
      return `add ${countSpaces(op.added)} after ${quote(contextBefore(line, op.col))}`;
    }
    if (atEnd) return `add ${quote(op.added)} at the end`;
    if (atStart) return `add ${quote(op.added)} at the beginning`;
    return `add ${quote(op.added)} after ${quote(contextBefore(line, op.col))}`;
  }
  if (!op.added) {
    if (isSpace(op.removed)) {
      if (atStart) return `remove ${countSpaces(op.removed)} of indentation`;
      return `remove ${countSpaces(op.removed)} after ${quote(contextBefore(line, op.col))}`;
    }
    const repeated = line.split(op.removed).length > 2;
    if (!repeated) return `remove ${quote(op.removed)}`;
    const next = contextAfter(line, op.col, op.removed);
    return next ? `remove ${quote(op.removed)} before ${quote(next)}` : `remove ${quote(op.removed)} at the end`;
  }
  if (isSpace(op.removed) && isSpace(op.added) && atStart) {
    const delta = op.added.length - op.removed.length;
    return delta > 0 ? `indent by ${countSpaces(op.added.slice(0, delta))} more`
      : `remove ${countSpaces(op.removed.slice(0, -delta))} of indentation`;
  }
  // A short piece that appears more than once on the line ("n" in "num <= n") is located by the code before it.
  const repeated = line.split(op.removed).length > 2;
  const before = repeated && !atStart ? contextBefore(line, op.col) : '';
  return `change ${quote(op.removed)} to ${quote(op.added)}${before ? ` after ${quote(before)}` : ''}`;
}

const lineRef = (from: number, to: number): string => from === to ? `Line ${from}` : `Lines ${from}–${to}`;

function shortCode(text: string, max = 40): string {
  const trimmed = text.trim();
  return trimmed && trimmed.length <= max ? ` (${quote(trimmed)})` : '';
}

interface Unit {
  kind: 'pair' | 'block';
  removes: DiffRow[];
  adds: DiffRow[];
  ops?: Op[];
  oldText?: string;
  signature?: string;
}

function pairLines(removed: DiffRow[], added: DiffRow[], removedText: string[], addedText: string[]): Unit[] {
  const units: Unit[] = [];
  let pendingRemoves: DiffRow[] = [], pendingAdds: DiffRow[] = [];
  let aPtr = 0;
  const flushBlock = () => {
    if (pendingRemoves.length || pendingAdds.length) {
      units.push({ kind: 'block', removes: pendingRemoves, adds: pendingAdds });
      pendingRemoves = []; pendingAdds = [];
    }
  };
  for (let r = 0; r < removed.length; r++) {
    let best = -1, bestScore = PAIR_THRESHOLD;
    for (let a = aPtr; a < Math.min(added.length, aPtr + 6); a++) {
      const score = similarity(removedText[r], addedText[a]);
      if (score > bestScore) { bestScore = score; best = a; }
    }
    if (best === -1) {
      pendingRemoves.push(removed[r]);
      continue;
    }
    for (let a = aPtr; a < best; a++) pendingAdds.push(added[a]);
    flushBlock();
    const inline = inlineDiff(removedText[r], addedText[best]);
    removed[r].segments = inline.before;
    removed[r].paired = true;
    added[best].segments = inline.after;
    added[best].paired = true;
    units.push({
      kind: 'pair', removes: [removed[r]], adds: [added[best]], ops: inline.ops, oldText: removedText[r],
      signature: inline.ops.map(op => `${op.removed}\u0000${op.added}`).join('\u0001')
    });
    aPtr = best + 1;
  }
  for (let a = aPtr; a < added.length; a++) pendingAdds.push(added[a]);
  flushBlock();
  return units;
}

function describePair(unit: Unit): string {
  const row = unit.removes[0];
  const ops = unit.ops || [];
  const line = unit.oldText || '';
  const body = ops.length > 3 && similarity(line, unit.adds[0].segments.map(s => s.text).join('')) < 0.6
    ? 'retype this line as shown in green'
    : ops.map(op => describeOp(op, line)).join('; ');
  return `Line ${row.oldLine}: ${body}.`;
}

function describeMergedPairs(units: Unit[]): string {
  const first = units[0], last = units[units.length - 1];
  const ops = first.ops || [];
  const body = ops.map(op => describeOp(op, first.oldText || '')).join('; ');
  return `${lineRef(first.removes[0].oldLine!, last.removes[0].oldLine!)}: ${body} on each line.`;
}

function describeBlock(unit: Unit, previousOldLine: number, newFile: boolean): string {
  const removes = unit.removes, adds = unit.adds;
  if (removes.length && adds.length) {
    const from = removes[0].oldLine!, to = removes[removes.length - 1].oldLine!;
    return `Replace ${lineRef(from, to).toLowerCase()} with the ${adds.length === 1 ? 'green line' : `${adds.length} green lines`}.`;
  }
  if (removes.length) {
    const from = removes[0].oldLine!, to = removes[removes.length - 1].oldLine!;
    return removes.length === 1
      ? `Delete line ${from}${shortCode(removes[0].segments.map(s => s.text).join(''))}.`
      : `Delete lines ${from}–${to}.`;
  }
  if (newFile) return `New file: type the ${adds.length === 1 ? 'line' : `${adds.length} lines`} shown in green.`;
  const where = previousOldLine === 0 ? 'at the very top' : `after line ${previousOldLine}`;
  return adds.length === 1
    ? `Add a new line ${where}${shortCode(adds[0].segments.map(s => s.text).join(''), 60)}.`
    : `Add the ${adds.length} green lines ${where}.`;
}

const normalize = (text: string): string => text.replace(/\s+/g, ' ').trim();

function rowsText(rows: DiffRow[]): string {
  return normalize(rows.map(row => row.segments.map(segment => segment.text).join('')).join('\n'));
}

/** Splits a pure add or remove run where anchored notes begin, so each explained section is its own hunk. */
function splitByAnchors(rows: DiffRow[], anchors: string[]): DiffRow[][] {
  const starts = new Set<number>();
  for (const anchor of anchors) {
    const firstLine = normalize(anchor.split('\n').find(line => line.trim()) || '');
    if (!firstLine) continue;
    const index = rows.findIndex(row => normalize(row.segments.map(s => s.text).join('')).includes(firstLine));
    if (index > 0) starts.add(index);
  }
  const cuts = [0, ...[...starts].sort((a, b) => a - b), rows.length];
  const groups: DiffRow[][] = [];
  for (let k = 0; k < cuts.length - 1; k++) if (cuts[k + 1] > cuts[k]) groups.push(rows.slice(cuts[k], cuts[k + 1]));
  return groups;
}

function makeHunk(kind: 'context' | 'change', rows: DiffRow[], steps: string[] = []): DiffHunk {
  const collapsible = kind === 'context' && rows.length > CONTEXT_COLLAPSE_AT;
  return {
    kind, rows, steps, notes: [], expanded: !collapsible,
    head: collapsible ? rows.slice(0, CONTEXT_EDGE) : rows,
    hidden: collapsible ? rows.slice(CONTEXT_EDGE, rows.length - CONTEXT_EDGE) : [],
    tail: collapsible ? rows.slice(rows.length - CONTEXT_EDGE) : []
  };
}

/**
 * Line diff for a reply that is still streaming in. Only the complete lines written so far are compared, and
 * they are matched from the top, so a half-written file never matches its last `}` to the old file's last `}`
 * and flashes the lines in between as removed. A new line that resembles the old line at the same point is
 * paired with it at once (shown as a change); old lines the reply has not reached yet are left out, because
 * they may still be kept. The finished reply is then diffed normally.
 */
export function streamingLineDiff(oldText: string | undefined, partialNew: string): DiffLine[] {
  const cut = partialNew.lastIndexOf('\n');
  const newLines = cut < 0 ? [] : partialNew.slice(0, cut).split('\n');
  const oldLines = oldText === undefined ? [] : oldText.split('\n');
  const m = oldLines.length, n = newLines.length;
  if (!n) return [];
  const lcs = Array.from({ length: m + 1 }, () => new Uint16Array(n + 1));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      lcs[i][j] = oldLines[i] === newLines[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const out: DiffLine[] = [];
  let i = 0, j = 0;
  while (j < n) {
    if (i < m && oldLines[i] === newLines[j]) {
      out.push({ type: 'unchanged', content: newLines[j] });
      i++; j++;
    } else if (i < m && lcs[i + 1][j] > lcs[i][j + 1]) {
      out.push({ type: 'remove', content: oldLines[i++] });
    } else if (i < m && lcs[i + 1][j] === lcs[i][j + 1] && similarity(oldLines[i], newLines[j]) > PAIR_THRESHOLD) {
      out.push({ type: 'remove', content: oldLines[i++] });
      out.push({ type: 'add', content: newLines[j++] });
    } else {
      out.push({ type: 'add', content: newLines[j++] });
    }
  }
  return out;
}

/**
 * Builds the reviewable diff for one file from a line diff. Notes whose anchor text appears in a
 * change hunk are attached to that hunk; the rest become file-level notes.
 */
export function buildFileDiff(lines: DiffLine[], notes: CodingNote[], newFile: boolean): FileDiff {
  const hunks: DiffHunk[] = [];
  const anchors = notes.map(note => note.anchor).filter(Boolean);
  let oldLine = 1, newLine = 1, index = 0;
  while (index < lines.length) {
    if (lines[index].type === 'unchanged') {
      const rows: DiffRow[] = [];
      while (index < lines.length && lines[index].type === 'unchanged') {
        rows.push({ kind: 'context', oldLine: oldLine++, newLine: newLine++,
          segments: [{ text: lines[index++].content, changed: false }] });
      }
      hunks.push(makeHunk('context', rows));
      continue;
    }
    const previousOldLine = oldLine - 1;
    const removed: DiffRow[] = [], added: DiffRow[] = [], removedText: string[] = [], addedText: string[] = [];
    while (index < lines.length && lines[index].type !== 'unchanged') {
      const current = lines[index++];
      if (current.type === 'remove') {
        removed.push({ kind: 'remove', oldLine: oldLine++, segments: [{ text: current.content, changed: true }] });
        removedText.push(current.content);
      } else {
        added.push({ kind: 'add', newLine: newLine++, segments: [{ text: current.content, changed: true }] });
        addedText.push(current.content);
      }
    }
    const units = pairLines(removed, added, removedText, addedText);
    // The old line a new line goes after: the last one this change region has already dealt with (a line changed
    // just before it, "WHERE age > 21;" -> "WHERE age > 21"), not the line before the region.
    let lastOldLine = previousOldLine;
    const passed = (unit: Unit) => {
      for (const row of unit.removes) lastOldLine = Math.max(lastOldLine, row.oldLine || 0);
    };
    let k = 0;
    while (k < units.length) {
      const unit = units[k];
      if (unit.kind === 'block') {
        const pureAdd = !unit.removes.length, pureRemove = !unit.adds.length;
        const groups = pureAdd ? splitByAnchors(unit.adds, anchors) : pureRemove ? splitByAnchors(unit.removes, anchors) : null;
        if (groups && groups.length > 1) {
          for (const group of groups) {
            const sub: Unit = { kind: 'block', removes: pureAdd ? [] : group, adds: pureAdd ? group : [] };
            hunks.push(makeHunk('change', group, [describeBlock(sub, lastOldLine, newFile)]));
          }
        } else {
          hunks.push(makeHunk('change', [...unit.removes, ...unit.adds], [describeBlock(unit, lastOldLine, newFile)]));
        }
        passed(unit);
        k++;
        continue;
      }
      let end = k + 1;
      while (end < units.length && units[end].kind === 'pair' && units[end].signature === unit.signature
          && (unit.ops || []).length === 1) end++;
      const run = units.slice(k, end);
      const rows = [...run.flatMap(u => u.removes), ...run.flatMap(u => u.adds)];
      hunks.push(makeHunk('change', rows, [run.length === 1 ? describePair(unit) : describeMergedPairs(run)]));
      run.forEach(passed);
      k = end;
    }
  }
  const fileNotes: string[] = [];
  for (const note of notes) {
    const anchor = normalize(note.anchor || '');
    const target = anchor && hunks.find(hunk => hunk.kind === 'change'
      && (rowsText(hunk.rows.filter(row => row.kind === 'add')).includes(anchor)
        || rowsText(hunk.rows.filter(row => row.kind === 'remove')).includes(anchor)));
    if (target) target.notes.push(note.text);
    else fileNotes.push(note.text);
  }
  const changeRows = hunks.filter(hunk => hunk.kind === 'change').flatMap(hunk => hunk.rows);
  return {
    hunks,
    changes: hunks.filter(hunk => hunk.kind === 'change').length,
    edits: changeRows.filter(row => row.kind === 'remove' || (row.kind === 'add' && !row.paired)).length,
    fileNotes
  };
}
